import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  getTicketById,
  replyToTicket,
  closeTicket,
  openTicket,
  assignTicket,
  snoozeTicket,
  getAdmins,
} from "services/intercom.service";
import {
  ClockIcon,
  EyeIcon,
  OutlinedCheckMark,
  ReplyIcon,
  StickyNotesIcon,
  UserCogIcon,
} from "assets/icons";
import {
  TextArea,
  CheckboxField,
  DatePickerField,
  FormButton,
  TimePickerField,
} from "components/formfields";
import { ModalLayout } from "components/modals";
import { toast } from "react-toastify";
import useIntercomStore from "(store)/useIntercomStore";
import parse from "html-react-parser";

// Validation schemas
const replyValidationSchema = Yup.object({
  message: Yup.string()
    .required("Reply message is required")
    .min(1, "Message cannot be empty"),
  isNote: Yup.boolean(),
});

const snoozeValidationSchema = Yup.object({
  snoozeDate: Yup.date()
    .required("Snooze date is required")
    .min(new Date(), "Date must be in the future"),
  snoozeTime: Yup.object().nullable(),
});

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messageEndRef = useRef(null);

  // Zustand store
  const {
    currentAdmin,
    adminsList,
    setCurrentAdmin,
    setAdminsList,
    getCurrentAdminId,
  } = useIntercomStore();

  // State management
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);

  // Effects
  useEffect(() => {
    fetchTicket();
    fetchAdmins();
  }, [id]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      setError(null); // Clear error after showing toast
    }
  }, [error]);

  // API calls
  const fetchTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTicketById(id);
      setTicket(response.data);
    } catch (err) {
      setError("Failed to fetch ticket details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await getAdmins();
      const admins = Array.isArray(response.data) ? response.data : [];

      // Update Zustand store
      setAdminsList(admins);
    } catch (err) {
      console.error("Failed to fetch admins list:", err);
      setError("Failed to fetch admins list");
    }
  };

  // Action handlers
  const handleSubmitReply = async (values, { resetForm }) => {
    const adminId = getCurrentAdminId();

    if (!adminId) {
      setError("Please select an admin to reply as");
      return;
    }

    setSubmitting(true);
    try {
      await replyToTicket(id, {
        message: values.message,
        isNote: values.isNote,
        adminId: adminId, // Pass the current admin ID
      });
      await fetchTicket();
      resetForm();
      toast.success(`${values.isNote ? "Note" : "Reply"} sent successfully!`);
    } catch (err) {
      setError(
        `Failed to send ${values.isNote ? "note" : "reply"}. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleTicketAction = async (action, successMessage) => {
    try {
      await action();
      await fetchTicket();
      if (successMessage) {
        toast.success(successMessage);
      }
    } catch (err) {
      setError("Failed to perform action. Please try again.");
    }
  };

  const handleAssignTicket = async (assigneeId) => {
    await handleTicketAction(
      () => assignTicket(id, assigneeId),
      assigneeId === "0"
        ? "Ticket unassigned successfully"
        : "Ticket assigned successfully"
    );
  };

  const handleSnoozeSubmit = async (values) => {
    try {
      let snoozedUntil;

      if (values.snoozeTime && values.snoozeTime.isValid()) {
        // If time is provided, combine date and time
        const dateStr = values.snoozeDate.toISOString().split("T")[0];
        const timeStr = values.snoozeTime.format("HH:mm:ss");
        snoozedUntil = new Date(`${dateStr}T${timeStr}`);
      } else {
        // If no time provided, default to 12:00:00
        const dateStr = values.snoozeDate.toISOString().split("T")[0];
        snoozedUntil = new Date(`${dateStr}T12:00:00`);
      }

      await snoozeTicket(id, snoozedUntil.toISOString());
      setShowSnoozeModal(false);
      await fetchTicket();
      toast.success("Ticket snoozed successfully!");
    } catch (err) {
      setError("Failed to snooze ticket. Please try again.");
    }
  };

  // Helper functions
  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Helper function to check if ticket is snoozed and format snooze date
  const getSnoozeInfo = () => {
    if (!ticket?.snoozed_until) return { isSnoozed: false, snoozeDate: null };
    const snoozeDate = new Date(ticket.snoozed_until * 1000);
    const isSnoozed = snoozeDate > new Date();
    return {
      isSnoozed,
      snoozeDate: isSnoozed ? snoozeDate.toLocaleString() : null,
    };
  };

  const getStatusBadge = (ticketState) => {
    const status = ticketState?.category || ticketState;
    const statusConfig = {
      submitted: { bg: "bg-blue-500", label: "Submitted" },
      in_progress: { bg: "bg-alpha", label: "In Progress" },
      resolved: { bg: "bg-green-500", label: "Resolved" },
      closed: { bg: "bg-gray-500", label: "Closed" },
    };

    const config = statusConfig[status] || { bg: "bg-gray-400", label: status };
    return (
      <span className={`badge ${config.bg} text-white border-none`}>
        {config.label}
      </span>
    );
  };

  const getAssignedAdmin = () => {
    if (!ticket.admin_assignee_id || ticket.admin_assignee_id === "0") {
      return "Unassigned";
    }

    const assignmentParts =
      ticket.ticket_parts?.ticket_parts?.filter(
        (part) =>
          part.part_type === "assignment" && part.assigned_to?.type === "admin"
      ) || [];

    if (assignmentParts.length > 0) {
      const latestAssignment = assignmentParts[assignmentParts.length - 1];
      return latestAssignment.assigned_to?.id
        ? latestAssignment.author?.name ||
            `Admin ID: ${latestAssignment.assigned_to.id}`
        : "Unassigned";
    }

    return `Admin ID: ${ticket.admin_assignee_id}`;
  };

  const getCustomerInfo = () => {
    if (ticket.contacts?.contacts?.length > 0) {
      const userPart = ticket.ticket_parts?.ticket_parts?.find(
        (part) => part.author?.type === "user"
      );
      
      return {
        name: userPart?.author?.name || "--",
        email: userPart?.author?.email || "--",
      };
    }
    return {
      name: "Internal Ticket",
      email: "N/A",
    };
  };

  const renderTicketParts = () => {
    if (!ticket.ticket_parts?.ticket_parts) return null;

    const conversationalParts = ticket.ticket_parts.ticket_parts.filter(
      (part) => part.part_type === "comment" || part.part_type === "note"
    );

    if (conversationalParts.length === 0) {
      return (
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          No conversation history available
        </div>
      );
    }

    return conversationalParts.map((part) => {
      const isAdmin = part.author?.type === "admin";
      const isNote = part.part_type === "note";

      return (
        <div key={part.id} className="mb-6">
          <div className="flex justify-between mb-2">
            <div>
              <span className="font-semibold">
                {part.author?.name || "Unknown"}
              </span>
              {isNote && (
                <span className="badge bg-alpha text-black border-none ml-2">
                  Internal Note
                </span>
              )}
            </div>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              {formatTime(part.created_at)}
            </span>
          </div>
          <div
            className={`p-4 rounded-lg ${
              isAdmin
                ? isNote
                  ? "bg-alpha bg-opacity-20"
                  : "bg-secondary bg-opacity-20 text-black dark:text-white"
                : "bg-gray-100 dark:bg-gray-800"
            }`}
          >
            {part.body ? parse(part.body) : "No content"}
          </div>
        </div>
      );
    });
  };

  const renderTicketActivity = () => {
    if (!ticket.ticket_parts?.ticket_parts) return null;

    const activityParts = ticket.ticket_parts.ticket_parts.filter(
      (part) => part.part_type !== "comment" && part.part_type !== "note"
    );

    if (activityParts.length === 0) return null;

    return (
      <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
        <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold">Ticket Activity</h2>
        </div>
        <div className="card-body p-4 max-h-[300px] overflow-y-auto">
          {activityParts.map((part) => (
            <div
              key={part.id}
              className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <span className="font-medium text-sm">
                    {part.author?.name || "System"}
                  </span>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {part.part_type === "assignment" && (
                      <>
                        {part.assigned_to?.type === "admin"
                          ? `Assigned ticket to ${
                              part.assigned_to?.id
                                ? `admin ${part.assigned_to.id}`
                                : "themselves"
                            }`
                          : "Unassigned ticket"}
                      </>
                    )}
                    {part.part_type === "ticket_state_updated_by_admin" && (
                      <>
                        Changed status from{" "}
                        <span className="font-medium">
                          {part.previous_ticket_state}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">{part.ticket_state}</span>
                      </>
                    )}
                    {part.part_type === "entity_linked" &&
                      "Linked ticket to conversation"}
                  </div>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTime(part.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-secondary"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Loading ticket details...
          </p>
        </div>
      </div>
    );
  }

  const customer = getCustomerInfo();
  const ticketStatus = ticket.ticket_state?.category || ticket.ticket_state;
  const { isSnoozed, snoozeDate } = getSnoozeInfo();

  return (
    <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-bgprimary">
      {/* Header */}
      <div className="p-5 border-b dark:border-gray-800">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Ticket #{ticket.ticket_id}
              {getStatusBadge(ticket.ticket_state)}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {ticket.ticket_attributes?._default_title_ || "No Title"}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Created: {formatTime(ticket.created_at)}
              {ticket.updated_at &&
                ` • Last Updated: ${formatTime(ticket.updated_at)}`}
            </p>
            {isSnoozed && (
              <p className="text-sm text-alpha dark:text-alpha">
                {`Ticket is snoozed until ${snoozeDate}. It will be active again after this time.`}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {ticket.open ? (
              <button
                onClick={() => handleTicketAction(() => closeTicket(id))}
                className="btn bg-secondary text-white gap-2 border-none hover:bg-secondary"
              >
                <OutlinedCheckMark /> Close Ticket
              </button>
            ) : (
              <button
                onClick={() => handleTicketAction(() => openTicket(id))}
                className="btn bg-secondary text-white gap-2 border-none hover:bg-secondary"
              >
                <EyeIcon color="white" /> Reopen Ticket
              </button>
            )}

            <button
              onClick={() => setShowSnoozeModal(true)}
              className="btn bg-alpha text-white gap-2 border-none hover:bg-alpha"
              disabled={isSnoozed || ticketStatus === "closed"}
            >
              <ClockIcon /> Snooze
            </button>

            <div className="dropdown dropdown-end">
              <label
                tabIndex={0}
                className="btn bg-primary text-white gap-2 border-none hover:bg-primary"
              >
                <UserCogIcon /> Assign
              </label>
              <ul
                tabIndex={0}
                className="dropdown-content z-[1] menu p-2 shadow bg-white rounded-box w-52 dark:bg-bgsecondary dark:text-white"
              >
                <li>
                  <a onClick={() => handleAssignTicket("0")}>Unassign</a>
                </li>
                {adminsList.map((admin) => (
                  <li key={admin.id}>
                    <a onClick={() => handleAssignTicket(admin.id)}>
                      {admin.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mx-5 mt-3">
          <span>{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="p-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Conversation & Description */}
          <div className="lg:col-span-2">
            {/* Description */}
            {ticket.ticket_attributes?._default_description_ && (
              <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
                <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="font-semibold">Description</h2>
                </div>
                <div className="p-4">
                  <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                    {ticket.ticket_attributes._default_description_}
                  </div>
                </div>
              </div>
            )}

            {/* Conversation */}
            <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
              <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold">Conversation</h2>
              </div>

              <div className="p-4 max-h-[500px] overflow-y-auto">
                {renderTicketParts()}
                <div ref={messageEndRef} />
              </div>

              {/* Reply Form */}
              <div className="card-footer bg-gray-50 dark:bg-bgprimary p-4 border-t border-gray-200 dark:border-gray-700">
                <Formik
                  initialValues={{ message: "", isNote: false }}
                  validationSchema={replyValidationSchema}
                  onSubmit={handleSubmitReply}
                >
                  {({ values, setFieldValue }) => (
                    <Form>
                      <div className="mb-4">
                        <TextArea
                          name="message"
                          placeholder="Type your reply..."
                          disabled={submitting}
                        />
                      </div>

                      <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-3">
                        <div className="flex items-center gap-2">
                          <CheckboxField
                            name="isNote"
                            label="Send as internal note (only visible to team)"
                            className="whitespace-nowrap"
                            checked={values.isNote}
                            onChange={(e) =>
                              setFieldValue("isNote", e.target.checked)
                            }
                          />
                        </div>

                        <FormButton
                          type="submit"
                          disabled={submitting}
                          isSubmitting={submitting}
                          text={
                            submitting ? (
                              "Sending..."
                            ) : (
                              <div className="flex items-center gap-2">
                                {values.isNote ? (
                                  <StickyNotesIcon />
                                ) : (
                                  <ReplyIcon />
                                )}
                                {values.isNote ? "Add Note" : "Send Reply"}
                              </div>
                            )
                          }
                          variant="filled"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>

            {/* Activity Timeline */}
            {renderTicketActivity()}
          </div>

          {/* Right Column - Ticket Details */}
          <div className="lg:col-span-1">
            {/* Customer Information */}
            <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
              <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold">Customer Information</h2>
              </div>
              <div className="card-body p-4">
                <div className="space-y-3">
                  <div>
                    <span className="font-semibold">Name:</span> {customer.name}
                  </div>
                  <div>
                    <span className="font-semibold">Email:</span>{" "}
                    {customer.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Ticket Details */}
            <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
              <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold">Ticket Details</h2>
              </div>
              <div className="px-0">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Status:</span>
                    <span>
                      {ticket.ticket_state?.internal_label || ticketStatus}
                    </span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Type:</span>
                    <div className="flex items-center gap-2">
                      <span>{ticket.ticket_type?.icon || "📋"}</span>
                      <span>{ticket.ticket_type?.name || "N/A"}</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Category:</span>
                    <span>{ticket.category || "N/A"}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Priority:</span>
                    <span
                      className={ticket.open ? "text-primary" : "text-gray-500"}
                    >
                      {ticket.open ? "Open" : "Closed"}
                    </span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Created:</span>
                    <span>{formatTime(ticket.created_at)}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Last Updated:</span>
                    <span>{formatTime(ticket.updated_at)}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="font-semibold">Assigned To:</span>
                    <span>{getAssignedAdmin()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Linked Objects */}
            {ticket.linked_objects?.data?.length > 0 && (
              <div className="card bg-white dark:bg-bgsecondary shadow-md mb-6">
                <div className="card-header bg-gray-50 dark:bg-bgprimary p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="font-semibold">Linked Items</h2>
                </div>
                <div className="card-body p-4">
                  {ticket.linked_objects.data.map((obj) => (
                    <div
                      key={obj.id}
                      className="mb-2 p-2 bg-gray-100 dark:bg-gray-800 rounded"
                    >
                      <span className="font-medium capitalize">{obj.type}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        #{obj.id}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Snooze Modal */}
      <ModalLayout
        isOpen={showSnoozeModal}
        onClose={() => setShowSnoozeModal(false)}
        title="Snooze Ticket"
      >
        <Formik
          initialValues={{ snoozeDate: null, snoozeTime: null }}
          validationSchema={snoozeValidationSchema}
          onSubmit={handleSnoozeSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="px-3 space-y-4">
                <DatePickerField
                  name="snoozeDate"
                  label="Snooze Until Date"
                  placeholder="Select date"
                  isRequired
                  minDate={new Date()}
                />

                <TimePickerField
                  name="snoozeTime"
                  label="Snooze Until Time (Optional)"
                  placeholder="Select time"
                  timeFormat="HH:mm"
                  use12Hours={false}
                  needConfirm={false}
                />

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowSnoozeModal(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <FormButton
                    type="submit"
                    disabled={isSubmitting}
                    isSubmitting={isSubmitting}
                    text={isSubmitting ? "Snoozing..." : "Snooze Ticket"}
                    variant="filled"
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </ModalLayout>
    </div>
  );
};

export default TicketDetails;
